import { useEffect, useRef, useCallback, useState } from "react";
import type { RefObject } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "../lib/utils"
import type { Message } from "../types/chat"
import { markdownComponents } from "../lib/markdown-components";
import { Chat } from "./icons";
import { ChatFeedbackButtons } from "./chat-feedback-buttons";
import { ChatFeedbackModal } from "./chat-feedback-modal";
import { MessageWithSources } from "./message-with-sources";
import { feedbackService } from "../services/feedbackService";
import { NegativeFeedbackReason, FeedbackType } from "@/types/feedback";
import { Source } from "../lib/sources-utils";

interface ChatMessagesProps {
  messages: Message[];
  messagesEndRef: RefObject<HTMLDivElement>;
  isLoading?: boolean;
  streamedMessage?: string;
  chatId?: string;
  sources?: Source[];
  onSourceClick?: (source: Source) => void;
}

export function ChatMessages({ 
  messages, 
  messagesEndRef, 
  isLoading, 
  streamedMessage, 
  chatId,
  sources = [],
  onSourceClick 
}: ChatMessagesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('positive');
  const [loadingText, setLoadingText] = useState("checking sources");
  
  // Refs for scrolling
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);

  // Handle loading text transition
  useEffect(() => {
    if (isLoading && !streamedMessage) {
      // Reset to "checking sources" when loading starts
      setLoadingText("checking sources");
      
      // Set timer to change to "thinking" after 3 seconds
      const timer = setTimeout(() => {
        setLoadingText("thinking");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, streamedMessage]);

  // Smooth scroll function
  const smoothScrollToElement = useCallback((element: HTMLElement | null) => {
    if (!element || !containerRef.current) return;
    
    const container = containerRef.current;
    const elementTop = element.offsetTop;
    const containerHeight = container.clientHeight;
    
    // Calculate the target scroll position to show the element at the top with some padding
    const targetScrollTop = Math.max(0, elementTop - 80); // 80px from top for better visibility
    
    // Smooth scroll animation
    const startScrollTop = container.scrollTop;
    const startTime = performance.now();
    const duration = 400; // 400ms for smooth animation
    
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      const newScrollTop = startScrollTop + (targetScrollTop - startScrollTop) * easeOutCubic;
      container.scrollTop = newScrollTop;
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  }, []);

  // Scroll to latest message when messages change or streaming updates
  useEffect(() => {
    const scrollToLatest = () => {
      if (streamedMessage && streamingMessageRef.current) {
        // When streaming, scroll to show the streaming message
        smoothScrollToElement(streamingMessageRef.current);
      } else if (messages.length > 0 && lastMessageRef.current) {
        // When messages update, scroll to show the last message
        smoothScrollToElement(lastMessageRef.current);
      }
    };

    // Use a small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToLatest, 10);
    return () => clearTimeout(timeoutId);
  }, [messages, streamedMessage, smoothScrollToElement]);

  // Scroll to bottom when new user message is added (immediate response)
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].role === "user") {
      const timeoutId = setTimeout(() => {
        if (lastMessageRef.current) {
          smoothScrollToElement(lastMessageRef.current);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, smoothScrollToElement]);

  // Check if we should show feedback buttons (chat has at least one assistant response)
  const hasAssistantResponse = messages.some(message => message.role === 'assistant');
  const lastMessage = messages[messages.length - 1];
  const showFeedback = hasAssistantResponse && lastMessage?.role === 'assistant' && !isLoading && !streamedMessage;

  // Feedback handlers
  const handleFeedbackSubmit = async (feedback: string, selectedReasons?: NegativeFeedbackReason[]) => {
    if (!chatId) return;
    try {
      await feedbackService.submitFeedback(chatId, feedbackType, feedback, selectedReasons);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleThumbsUp = () => {
    setFeedbackType('positive');
    setShowFeedbackModal(true);
  };

  const handleThumbsDown = () => {
    setFeedbackType('negative');
    setShowFeedbackModal(true);
  };

  return (
    <div 
      ref={containerRef} 
      className="space-y-6 max-w-4xl mx-auto chat-container"
    >
      {/* Empty state */}
      {messages.length === 0 && !streamedMessage ? (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-6">
            <Chat size={48} />
          </div>
          <h2 className="text-2xl font-bold mb-2">What are you working on?</h2>
          <p className="text-zinc-400 text-center max-w-md">
            Ask me anything about your car.
          </p>
        </div>
      ) : (
        <>
          {/* Static messages */}
          {messages.map((message, index) => {
            const isLastAssistantMessage = index === messages.length - 1 && message.role === 'assistant';
            return (
              <div
                key={`${message.role}-${index}`}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}
              >
                <div className={cn("max-w-[85%] sm:max-w-[75%]", message.role === "user" ? "" : "w-full")}>
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-3 break-words",
                      message.role === "user"
                        ? "bg-emerald-600 text-white rounded-tr-none"
                        : "bg-zinc-800 text-white rounded-tl-none",
                    )}
                  >
                    {message.role === "assistant" ? (
                      <MessageWithSources
                        content={message.content}
                        sources={sources}
                        onSourceClick={onSourceClick}
                      />
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {message.content}
                      </ReactMarkdown>
                    )}
                  </div>
                  
                  {/* Show feedback buttons under last assistant message */}
                  {isLastAssistantMessage && showFeedback && (
                    <ChatFeedbackButtons
                      onThumbsUp={handleThumbsUp}
                      onThumbsDown={handleThumbsDown}
                      className="ml-4"
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Streaming message */}
          {streamedMessage && (
            <div ref={streamingMessageRef} className="flex justify-start animate-fade-in">
              <div className="rounded-2xl px-4 py-3 max-w-[85%] sm:max-w-[75%] bg-zinc-800 text-white rounded-tl-none break-words">
                <MessageWithSources
                  content={streamedMessage}
                  sources={sources}
                  onSourceClick={onSourceClick}
                />
                {/* Cursor effect */}
                <span className="inline-block w-2 h-5 bg-emerald-400 ml-1 cursor-blink"></span>
              </div>
            </div>
          )}

          {/* Thinking placeholder message */}
          {isLoading && !streamedMessage && (
            <div className="flex justify-start animate-fade-in">
              <div className="rounded-2xl px-4 py-3 max-w-[85%] sm:max-w-[75%] bg-zinc-800 text-white rounded-tl-none break-words">
                <div className="flex flex-col items-start space-y-2">
                  <span className="text-zinc-400 italic transition-all duration-500 ease-in-out">
                    {loadingText}...
                  </span>
                  {/* Animated dots underneath */}
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      <div ref={messagesEndRef} />

      {/* Feedback Modal */}
      <ChatFeedbackModal
        isOpen={showFeedbackModal}
        feedbackType={feedbackType}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
}